import { getAiUsageSummary, type AiUsageSummary } from '@/lib/api/master/ai-usage-api'
import { useEffect, useState } from 'react'
import { MdAutoAwesome, MdRefresh } from 'react-icons/md'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AiPricingForm } from './ai-pricing-form'
import { AiUsageOverview } from './ai-usage-overview'

type Tab = 'usage' | 'pricing'

const SAO_PAULO_DAY = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
})

function today(): string {
    return SAO_PAULO_DAY.format(new Date())
}

function daysAgo(days: number): string {
    return SAO_PAULO_DAY.format(new Date(Date.now() - days * 86_400_000))
}

const PRESETS: Array<{ label: string; range: () => [string, string] }> = [
    { label: 'Hoje', range: () => [today(), today()] },
    { label: '7 dias', range: () => [daysAgo(6), today()] },
    { label: '30 dias', range: () => [daysAgo(29), today()] },
    { label: 'Este mês', range: () => [`${today().slice(0, 8)}01`, today()] },
]

/**
 * Consumo de IA da plataforma: quanto custa, onde se gasta, por organização e
 * por modelo, e a tabela de preços que dá o custo. Ver docs/ai-usage-billing.md.
 */
export default function AiUsagePage() {
    const [searchParams, setSearchParams] = useSearchParams()
    const tab: Tab = searchParams.get('tab') === 'pricing' ? 'pricing' : 'usage'
    const from = searchParams.get('from') ?? `${today().slice(0, 8)}01`
    const to = searchParams.get('to') ?? today()

    const [summary, setSummary] = useState<AiUsageSummary | null>(null)
    const [loading, setLoading] = useState(false)
    const [reloadKey, setReloadKey] = useState(0)

    const updateParams = (patch: Record<string, string>) => {
        const next = new URLSearchParams(searchParams)
        for (const [key, value] of Object.entries(patch)) next.set(key, value)
        setSearchParams(next, { replace: true })
    }

    useEffect(() => {
        if (tab !== 'usage') return
        if (from > to) return
        let cancelled = false
        setLoading(true)
        void (async () => {
            const response = await getAiUsageSummary(from, to)
            if (cancelled) return
            setLoading(false)
            if (response.success && response.result) setSummary(response.result)
            else toast.error(response.error || 'Não foi possível carregar o consumo de IA')
        })()
        return () => {
            cancelled = true
        }
    }, [tab, from, to, reloadKey])

    return (
        <div className="p-4 sm:p-6 space-y-6">
            <div>
                <h1 className="text-2xl font-semibold flex items-center gap-2">
                    <MdAutoAwesome className="text-primary" />
                    Consumo de IA
                </h1>
                <p className="text-sm text-base-content/60 mt-0.5">
                    Custo de todas as chamadas pagas à OpenAI, em todas as organizações
                </p>
            </div>

            <div role="tablist" className="tabs tabs-border">
                <button
                    type="button"
                    role="tab"
                    className={`tab ${tab === 'usage' ? 'tab-active' : ''}`}
                    onClick={() => updateParams({ tab: 'usage' })}
                >
                    Consumo
                </button>
                <button
                    type="button"
                    role="tab"
                    className={`tab ${tab === 'pricing' ? 'tab-active' : ''}`}
                    onClick={() => updateParams({ tab: 'pricing' })}
                >
                    Tabela de preços
                </button>
            </div>

            {tab === 'pricing' ? (
                <AiPricingForm />
            ) : (
                <>
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="form-control">
                            <label className="label pb-1.5" htmlFor="ai-usage-from">
                                <span className="label-text font-medium">De</span>
                            </label>
                            <input
                                id="ai-usage-from"
                                type="date"
                                className="input input-bordered input-sm"
                                value={from}
                                max={to}
                                onChange={event =>
                                    event.target.value && updateParams({ from: event.target.value })
                                }
                            />
                        </div>
                        <div className="form-control">
                            <label className="label pb-1.5" htmlFor="ai-usage-to">
                                <span className="label-text font-medium">Até</span>
                            </label>
                            <input
                                id="ai-usage-to"
                                type="date"
                                className="input input-bordered input-sm"
                                value={to}
                                min={from}
                                onChange={event =>
                                    event.target.value && updateParams({ to: event.target.value })
                                }
                            />
                        </div>
                        <div className="join">
                            {PRESETS.map(preset => {
                                const [presetFrom, presetTo] = preset.range()
                                const active = presetFrom === from && presetTo === to
                                return (
                                    <button
                                        key={preset.label}
                                        type="button"
                                        className={`btn btn-sm join-item ${active ? 'btn-primary' : 'btn-outline'}`}
                                        onClick={() => updateParams({ from: presetFrom, to: presetTo })}
                                    >
                                        {preset.label}
                                    </button>
                                )
                            })}
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost"
                            onClick={() => setReloadKey(key => key + 1)}
                            disabled={loading}
                            title="Atualizar"
                        >
                            <MdRefresh className={loading ? 'animate-spin' : ''} />
                            Atualizar
                        </button>
                    </div>

                    {loading && !summary ? (
                        <div className="flex justify-center py-20">
                            <span className="loading loading-spinner loading-lg text-primary" />
                        </div>
                    ) : summary ? (
                        <div className={loading ? 'opacity-60 transition-opacity' : ''}>
                            <AiUsageOverview summary={summary} />
                        </div>
                    ) : null}
                </>
            )}
        </div>
    )
}
